import { apiStatus } from '@va/constants';
import { getFeaturePermissionsStatus, isCompanyRevealFeatureEnabled } from '@va/dashboard/selectors/app';
import { useReportBlockNodeBuilder } from '@va/dashboard/shared/report-block-nodes';
import { useVisitsByCompany } from '@va/dashboard/shared/visitors';
import { useTranslate } from '@va/localization';
import { AppliedFilters, FiltersContextProvider, useFiltersContext } from '@va/shared/feature-filters';
import { DataViewOption } from '@va/types/report-block';
import { SortingDirection } from '@va/types/requests';
import { DataTableV8, useControlledTableState } from '@va/ui/components/data-table';
import { ReportBlock, useReportBlockCtx } from '@va/ui/components/report-block';
import { ReportBlockNodeIds } from '@va/ui/components/report-block/nodes';
import { useRefreshCallbacks } from '@va/util/components';
import { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useVisitsByCompanyColumns } from './useVisitsByCompanyColumns';
import { useVisitsByCompanyFilterOptions } from './useVisitsByCompanyFilters';

const id = 'visitor-company-distribution';

export const VisitsByCompanyCard = memo(() => {
  const translate = useTranslate();
  const buildReportBlockNode = useReportBlockNodeBuilder();
  const nodes = useMemo(
    () => ({
      appliedFilters: <AppliedFilters />,
      filterButton: buildReportBlockNode(ReportBlockNodeIds.FILTER_BUTTON),
    }),
    [buildReportBlockNode],
  );

  const visualizationComponents = useMemo(() => ({ [DataViewOption.horizontalBarChart]: VisitsByCompanyTable }), []);
  const filterOptions = useVisitsByCompanyFilterOptions();
  return (
    <FiltersContextProvider reportBlockId={id} allFilterOptions={filterOptions}>
      <ReportBlock
        id={id}
        title={translate('all.companyVisits.card.title')}
        visualization={{ selectedView: DataViewOption.horizontalBarChart }}
        visualizationComponents={visualizationComponents}
        nodes={nodes}
      />
    </FiltersContextProvider>
  );
});

const VisitsByCompanyTable = () => {
  const { id, paginationId } = useReportBlockCtx();
  const { pagination, setPagination } = useControlledTableState(undefined, id);
  const { createApiConditions } = useFiltersContext();
  const featurePermissionsStatus = useSelector(getFeaturePermissionsStatus);

  const showIp2Company = useSelector(isCompanyRevealFeatureEnabled);

  const shouldFetchData = useMemo(() => {
    return featurePermissionsStatus === apiStatus.SUCCEEDED;
  }, [featurePermissionsStatus]);

  const { isLoading, data, mutate } = useVisitsByCompany({
    shouldFetch: shouldFetchData,
    fetchCompanyName: showIp2Company,
    pagination: { pageSize: pagination.pageSize, page: pagination.pageNumber },
    where: { and: createApiConditions() },
    order: [{ member: 'sessionCount', direction: SortingDirection.Desc }],
  });

  const columns = useVisitsByCompanyColumns();
  const { isManualRefreshing } = useRefreshCallbacks(mutate);

  return (
    <DataTableV8
      id='visits-by-company'
      isLoading={isLoading || isManualRefreshing}
      data={data?.payload ?? []}
      columns={columns}
      state={{ pagination }}
      rowCount={data?.meta.total ?? 0}
      onPaginationChange={setPagination}
      tableStyle={{ gridTemplateColumns: 'auto 1fr auto' }}
      paginationContainerId={paginationId}
    />
  );
};
